"use client";

import { usePathname } from "next/navigation";
import { destroyCookie } from "nookies";
import { useEffect, useRef } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { snoozeSupportCampaigns } from "@/lib/support/campaign-snooze";
import { instalarEventosDeSuporte } from "@/lib/support/events";
import { toE164 } from "@/lib/support/phone-e164";
import { installSupportMetrics } from "@/lib/support/posthog-metrics";
import { chatwootEstaAtivo } from "@/lib/support/provedor-ativo";
import { useGetUserInfo } from "@/services/queries/useGetUserInfo";
import { useEntryDialogBusy } from "@/store/entry-modals";

/**
 * Loader do widget do Chatwoot, o atendimento do produto.
 *
 * CHAT002-4226 (17/09/2026): a conta do Crisp foi suspensa e o Paulo mandou
 * virar para o Chatwoot na hora, com o balão aparecendo para quem está logado.
 * Por isso este componente:
 *   - monta quando o Chatwoot é o provedor ativo (`provedor-ativo.ts`, padrão
 *     fixo no código, sem variável de ambiente);
 *   - só carrega o SDK com usuário logado e fora das telas de login;
 *   - esconde o balão e zera a sessão do widget quando a pessoa sai.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * SEGURANÇA — identidade assinada (HMAC), já ativa
 * ────────────────────────────────────────────────────────────────────────────
 * Sem assinatura, qualquer pessoa abriria o console e executaria
 *
 *     window.$chatwoot.setUser("id-de-outro-cliente", { ... })
 *
 * passando a ser tratada como aquele contato e LENDO O HISTÓRICO DE SUPORTE
 * DELE. Num escritório de advocacia isso é sigilo de cliente.
 *
 * Fechado em 31/07: o inbox está com `hmac_mandatory: true` e a assinatura vem
 * de `GET /suporte/identidade` no api-core, que deriva o identificador da
 * SESSÃO autenticada — nunca de parâmetro do cliente, senão o endpoint viraria
 * um oráculo de assinaturas e o buraco continuaria.
 *
 * ⚠️ Consequência operacional: o endpoint precisa existir no ambiente. Em
 * produção, enquanto `CHATWOOT_HMAC_TOKEN` não estiver na stack, ele responde
 * 503 e o widget fica ANÔNIMO (degradação proposital, não quebra).
 */

const BASE_URL = "https://chatwoot.chatadv.com.br";
/**
 * Token da caixa "Suporte ChatADV" (inbox 1, site app2.chatadv.com.br).
 *
 * É PÚBLICO por desenho: o SDK o manda para o navegador de qualquer visitante,
 * igual ao website id do Crisp. O que é segredo é a chave HMAC, que fica só no
 * api-core (`CHATWOOT_HMAC_TOKEN`) e assina a identidade em `/suporte/identidade`.
 * Lido do próprio Chatwoot em 17/09/2026 (Channel::WebWidget id 1).
 */
const WEBSITE_TOKEN = "MNmDcJBFkdAoz9z76osB7iHQ";

/**
 * Telas em que o balão de suporte NÃO aparece (CHAT002-4661, 25/09/2026).
 *
 * São as telas de IA: nelas o campo de mensagem vai até a borda direita e o
 * botão de enviar fica no mesmo canto do balão. Medido no Chatwoot: das 51
 * conversas abertas pelo balão entre 17 e 24/09, 11 a 13 (22–25%) pediam uma
 * peça, petição ou análise, achando que o balão era a IA. Nessas telas o
 * suporte continua no "?" do topo ("Atendimento Humano") e no menu da conta.
 */
export const ROUTES_WITHOUT_BUBBLE = ["/agents/v2/conversation", "/analyzes-summary", "/pecas-v2", "/legal-templates", "/welcome-pecas",
// CHAT002-4991 (09/10/2026): na aula da Academy o balão cobria o cartão
// "Fazer agora na IA" e o texto no celular (relatório de 09/10). O suporte
// segue no "?" do topo e no menu da conta.
"/academy/aulas",
// CHAT002-5533 (23/10/2026): no checkout a pílula "Suporte" ficava em cima do
// "Precisa de ajuda?" do formulário, que leva o contexto do checkout. O CSS do
// CheckoutForm (CHAT002-4238) deixou de esconder o balão no formato
// expanded_bubble (36949d83e). O chat continua abrindo pelo botão do formulário.
"/pricing/checkout"] as const;
export function isRouteWithoutBubble(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return ROUTES_WITHOUT_BUBBLE.some(rota => pathname === rota || pathname.startsWith(`${rota}/`));
}
declare global {
  interface Window {
    chatwootSettings?: Record<string, unknown>;
    chatwootSDK?: {
      run: (opts: {
        websiteToken: string;
        baseUrl: string;
      }) => void;
    };
    $chatwoot?: {
      setUser: (id: string, attrs: Record<string, unknown>) => void;
      setCustomAttributes: (attrs: Record<string, unknown>) => void;
      reset?: () => void;
      toggleBubbleVisibility?: (estado: "show" | "hide") => void;
    };
  }
}

/** Quem usou o widget por último neste navegador (CHAT002-4226). */
export const CHAVE_ULTIMO_USUARIO_DO_CHAT = "@CHATADV:suporte-ultimo-usuario";

/**
 * Diz se quem entra agora NÃO é quem usou o widget por último neste navegador,
 * e grava o atual.
 *
 * O logout pela tela zera o widget, mas sessão expirada recarrega a página
 * direto no login sem passar por ele, e o cookie da conversa
 * (`cw_conversation`, no domínio do app) sobrevive. Sem esta conferência, o
 * próximo usuário do computador do escritório abriria a conversa do anterior.
 * Sem registro anterior também conta como troca: descartar uma conversa
 * anônima é barato; mostrar a de outra pessoa não é.
 */
function trocouDeUsuario(usuarioId: string): boolean {
  try {
    const anterior = window.localStorage.getItem(CHAVE_ULTIMO_USUARIO_DO_CHAT);
    window.localStorage.setItem(CHAVE_ULTIMO_USUARIO_DO_CHAT, usuarioId);
    return anterior !== usuarioId;
  } catch {
    // Sem storage não dá para saber: trata como troca.
    return true;
  }
}

/** Último telefone mandado ao Chatwoot por este navegador (CHAT002-4230). */
export const CHAVE_ULTIMO_TELEFONE_DO_CHAT = "@CHATADV:suporte-ultimo-telefone";

/**
 * Diz se o telefone a mandar mudou desde o último envio deste navegador, e
 * grava o atual.
 *
 * O SDK do Chatwoot guarda no cookie `cw_user_<token>` um resumo do último
 * setUser (nome, e-mail, avatar e identidade, SEM o telefone) e não reenvia
 * quando o resumo bate. Sem esta conferência, quem já tinha sido identificado
 * nunca teria o telefone gravado no contato, nem a troca de telefone.
 */
function telefoneMudou(telefone: string): boolean {
  try {
    const anterior = window.localStorage.getItem(CHAVE_ULTIMO_TELEFONE_DO_CHAT);
    window.localStorage.setItem(CHAVE_ULTIMO_TELEFONE_DO_CHAT, telefone);
    return anterior !== telefone;
  } catch {
    // Sem storage não dá para saber, e forçar o reenvio a cada carga só custa uma chamada.
    return true;
  }
}

/** Apaga a conversa guardada pelo SDK antes de ele subir (ele lê o cookie ao criar o iframe). */
function descartarConversaGuardada(): void {
  destroyCookie(null, "cw_conversation", {
    path: "/"
  });
  destroyCookie(null, `cw_user_${WEBSITE_TOKEN}`, {
    path: "/"
  });
}

/**
 * Remove chaves sem conteúdo do payload de atributos.
 *
 * O Chatwoot renderiza na lateral toda chave que ele TEM guardada, mesmo com
 * valor vazio. Sem esta limpeza, um usuário que não preencheu o cadastro vira
 * uma lista de vinte linhas em branco e o atendente perde de vista o que
 * importa (plano e trial).
 *
 * `false` e `0` são conteúdo — "não verificou o WhatsApp" e "0 dias restantes"
 * são exatamente o tipo de informação que o atendente precisa ver.
 */
function withoutEmpty(attrs: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(attrs).filter(([, value]) => value !== "" && value !== null && value !== undefined));
}
export function ChatwootLoader() {
  const pathname = usePathname();
  const telaDeLogin = pathname?.startsWith("/auth") ?? false;
  const {
    data: user
  } = useGetUserInfo();
  const {
    plan
  } = useSubscription();
  const {
    phase,
    daysRemaining
  } = useTrialStatus();
  const usuarioId = user?.id ? String(user.id) : null;
  const logado = usuarioId !== null;
  // CHAT002-4983 (P11): enquanto uma janela de entrada está em cena (convite
  // da IA, boas-vindas, questionário), o balão some. Ele fica acima de
  // qualquer janela do app, e no celular a pílula "Fale com o suporte" cai
  // em cima do botão de baixo da janela: o toque abria o suporte.
  const entryDialogBusy = useEntryDialogBusy();
  const hideBubble = isRouteWithoutBubble(pathname) || entryDialogBusy;
  // Primeiro acesso pela conta do servidor (nenhum acesso registrado ainda):
  // as campanhas do balão esperam (ver `lib/support/campaign-snooze`). Ref
  // porque o efeito que carrega o SDK só re-roda com o usuário e a rota.
  const firstAccessRef = useRef(false);
  firstAccessRef.current = user?.jornada?.acessos === 0;
  const readyRef = useRef(false);
  const identifiedRef = useRef<string | null>(null);
  // Lido por callbacks que não re-rodam com a rota (o `chatwoot:ready` e o
  // retorno de sessão abaixo): precisam da tela ATUAL, não da do efeito.
  const hideBubbleRef = useRef(hideBubble);
  hideBubbleRef.current = hideBubble;

  // 1) Carrega o SDK quando há alguém logado, fora das telas de login.
  useEffect(() => {
    if (!chatwootEstaAtivo() || usuarioId === null || telaDeLogin) return;

    // Liga o contador de não lidas e as métricas do widget (idempotente).
    const desligarEventos = instalarEventosDeSuporte();
    const teardownMetrics = installSupportMetrics();
    const teardown = () => {
      desligarEventos();
      teardownMetrics();
    };
    const outraPessoa = trocouDeUsuario(usuarioId);

    // Nova sessão na mesma aba (o login é SPA): o SDK já está na página e só
    // o balão precisa voltar, com a conversa zerada se a pessoa mudou.
    if (document.getElementById("chatwoot-sdk")) {
      try {
        if (outraPessoa) {
          window.$chatwoot?.reset?.();
          identifiedRef.current = null;
        }
        window.$chatwoot?.toggleBubbleVisibility?.(hideBubbleRef.current ? "hide" : "show");
      } catch {
        // Widget em estado estranho não pode derrubar a tela.
      }
      return teardown;
    }
    if (outraPessoa) descartarConversaGuardada();
    // Antes do SDK subir: o widget lê o adiamento das campanhas ao carregar.
    if (firstAccessRef.current) snoozeSupportCampaigns();

    // `expanded_bubble` + `launcherTitle` (CHAT002-4661): o balão vira uma
    // pílula com o texto "Fale com o suporte". Antes era `standard`, a bolha
    // CIRCULAR do Crisp, e 22–25% das conversas pediam peça ou análise
    // achando que ali era a IA. O texto diz, antes do clique, que é suporte.
    // `darkMode: auto` acompanha o tema da página, igual ao que o
    // CrispLoader já faz com `$crisp.push(["config","color:mode",...])`.
    window.chatwootSettings = {
      locale: "pt_BR",
      position: "right",
      type: "expanded_bubble",
      // (CHAT002-5488, Paulo 22/10/2026: "acho muito grande o Fale com o
      // suporte") só "Suporte", numa pílula mais baixa (globals.css).
      launcherTitle: "Suporte",
      darkMode: "auto"
    };
    const onReady = () => {
      readyRef.current = true;
      // O SDK sobe com o balão visível: numa tela de IA ele some já.
      if (hideBubbleRef.current) {
        try {
          window.$chatwoot?.toggleBubbleVisibility?.("hide");
        } catch {
          // Widget em estado estranho não pode derrubar a tela.
        }
      }
    };
    window.addEventListener("chatwoot:ready", onReady);
    const script = document.createElement("script");
    script.id = "chatwoot-sdk";
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.defer = true;
    script.async = true;
    script.onload = () => {
      window.chatwootSDK?.run({
        websiteToken: WEBSITE_TOKEN,
        baseUrl: BASE_URL
      });
    };
    document.body.appendChild(script);
    return () => {
      window.removeEventListener("chatwoot:ready", onReady);
      teardown();
    };
  }, [usuarioId, telaDeLogin]);

  // 1b) Sem usuário carregado ou na tela de login: o balão some, e SÓ isso.
  //
  // ⚠️ Não zerar a conversa aqui. Trocar de cliente ou de escritório faz
  // `queryClient.clear()` com a pessoa ainda logada, e o usuário some por um
  // instante: um `reset()` neste ponto apagaria a conversa em andamento (a
  // revisão do CHAT002-4226 provou com o TanStack Query de verdade). Quem zera é
  // o logout (`encerrarSessaoDoChat`) e a conferência de usuário no carregamento.
  useEffect(() => {
    if (logado && !telaDeLogin) return;
    try {
      window.$chatwoot?.toggleBubbleVisibility?.("hide");
    } catch {
      // Widget em estado estranho não pode derrubar a tela.
    }
  }, [logado, telaDeLogin]);

  // 1c) Logado: nas telas de IA o balão some, nas demais volta (CHAT002-4661).
  // Esconde só o balão: aberto pelo "?" do topo ("Atendimento Humano"), o
  // chat continua funcionando nessas telas. Some também enquanto uma janela
  // de entrada está aberta (CHAT002-4983) e volta quando ela fecha.
  useEffect(() => {
    if (!logado || telaDeLogin) return;
    try {
      window.$chatwoot?.toggleBubbleVisibility?.(hideBubble ? "hide" : "show");
    } catch {
      // Widget em estado estranho não pode derrubar a tela.
    }
  }, [logado, telaDeLogin, hideBubble]);

  // 2) Identifica o usuário quando os dados chegam.
  //
  // Mesma estrutura do PosthogProvider: os três hooks resolvem em momentos
  // diferentes, então o efeito re-roda e só dispara quando há `user.id`.
  // A chave de identidade evita reenviar o mesmo payload a cada render.
  useEffect(() => {
    if (!chatwootEstaAtivo() || !user?.id) return;

    // O que o atendente vê na lateral da conversa.
    //
    // ⚠️ As chaves aqui TÊM que existir em Configurações › Atributos
    // personalizados do Chatwoot. Ele armazena qualquer chave que receba,
    // mas só RENDERIZA no painel as que estão definidas — foi por isso que
    // "plano" não aparecia na primeira tentativa.
    //
    // Campos de documento e endereço (cpfcnpj, address, postal_code) ficam
    // DE FORA de propósito: o atendente não precisa deles para responder
    // uma dúvida, e o painel de suporte é lido por mais gente do que o
    // painel administrativo. Quem precisar do cadastro completo clica em
    // `link_admin`.
    const attributes = withoutEmpty({
      plano: plan || "sem plano",
      fase_trial: phase ?? "n/a",
      dias_restantes: daysRemaining ?? 0,
      // `link_admin` NÃO é montado aqui: vem de `/suporte/identidade`.
      // A única ficha por usuário do admin é a da jornada de trials, e
      // ela só resolve quem está no snapshot (2,7% dos usuários, medido
      // em 01/08). Montado no navegador, o link abria "Trial não
      // encontrado" para quase todo mundo. Só o servidor sabe se existe.
      cliente_desde: user.created_at?.slice(0, 10) ?? "",
      oab: user.oab ?? "",
      escritorio: user.nome_do_escritorio ?? "",
      empresa: user.company ?? "",
      ocupacao: user.ocupacao ?? "",
      tempo_experiencia: user.tempo_experiencia ?? "",
      areas_atuacao: (user.areasAtuacao ?? []).map(a => a.nome).join(", "),
      telefone: user.phone_number ?? "",
      cidade: user.city ?? "",
      estado: user.estado ?? "",
      // Respostas do formulário de qualificação: dizem o que a pessoa
      // veio buscar, que costuma explicar a dúvida dela.
      setor: user.setor ?? "",
      tamanho_time: user.tamanho_time ?? "",
      expectativa_uso: user.expectativa_uso ?? "",
      gargalo: user.principal_gargalo_escritorio ?? "",
      whatsapp_verificado: Boolean(user.whatsapp_verificado),
      // `email_verified_at` vem escondido pela API (User::$hidden) e dava
      // "não" para todo mundo; a confirmação chega na `jornada` (CHAT002-5442).
      email_verificado: Boolean(user.jornada?.email_confirmado),
      onboarding_completo: Boolean(user.has_completed_onboarding),
      beta_pecas: Boolean(user.beta_pecas),
      beta_notificacao: Boolean(user.beta_notification),
      user_id: user.id
    });

    // A chave é derivada do payload INTEIRO, não de `id-plano-fase`. Se
    // olhasse só três campos, o usuário que verificasse o WhatsApp ou
    // terminasse o onboarding no meio da sessão continuaria aparecendo com
    // o dado velho para o atendente.
    const identityKey = JSON.stringify([user.name, user.email, attributes]);
    if (identifiedRef.current === identityKey) return;
    const identifyUser = async () => {
      if (!window.$chatwoot) return false;

      // A assinatura vem do api-core, que deriva o identificador da sessão
      // autenticada. Se o endpoint não responder (503 = sem chave, 401 =
      // sessão expirada), NÃO identificamos: o widget segue anônimo, que é
      // degradação aceitável. O que não pode é identificar sem assinatura.
      let identity: {
        identifier: string;
        identifier_hash: string;
        link_admin?: string | null;
      } | null = null;
      try {
        const r = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/suporte/identidade`, {
          credentials: "include",
          headers: {
            Accept: "application/json"
          }
        });
        if (!r.ok) return true; // não re-tentar em loop
        identity = await r.json();
      } catch {
        return true;
      }
      if (!identity?.identifier_hash) return true;

      // O telefone vai no campo do contato só quando o WhatsApp foi
      // verificado (o api-core zera a verificação quando o número muda) e
      // em E.164. No Chatwoot o telefone também serve para juntar contatos,
      // então número não conferido fica só no atributo `telefone`.
      const phoneNumber = user.whatsapp_verificado ? toE164(user.phone_number) : null;
      // O SDK não reenvia o setUser quando só o telefone mudou (o resumo
      // dele não inclui o telefone). Apagar o resumo força o reenvio; a
      // conversa (`cw_conversation`) continua intacta.
      if (telefoneMudou(phoneNumber ?? "")) {
        destroyCookie(null, `cw_user_${WEBSITE_TOKEN}`, {
          path: "/"
        });
      }
      window.$chatwoot.setUser(identity.identifier, {
        name: user.name,
        email: user.email,
        ...(phoneNumber ? {
          phone_number: phoneNumber
        } : {}),
        identifier_hash: identity.identifier_hash
      });
      // `withoutEmpty` derruba o link quando o servidor manda null, então
      // o atributo simplesmente não aparece na lateral em vez de aparecer
      // apontando para uma página de erro.
      window.$chatwoot.setCustomAttributes(withoutEmpty({
        ...attributes,
        link_admin: identity.link_admin
      }));
      identifiedRef.current = identityKey;
      return true;
    };

    // O SDK pode ainda não ter terminado de subir quando os dados do usuário
    // chegam, e vice-versa. Por isso tenta agora E escuta o `chatwoot:ready`:
    // o que acontecer por último dispara a identificação.
    //
    // `identifyUser` é assíncrono (busca a assinatura no api-core), então NÃO
    // dá para usar seu retorno como condição síncrona.
    let cancelled = false;
    const onReady_0 = () => {
      if (!cancelled) void identifyUser();
    };
    window.addEventListener("chatwoot:ready", onReady_0);
    void identifyUser();
    return () => {
      cancelled = true;
      window.removeEventListener("chatwoot:ready", onReady_0);
    };
    // Depende do objeto `user` inteiro, não de campo a campo: o TanStack
    // Query faz structural sharing, então a referência só muda quando o
    // dado muda de verdade. Listar os vinte campos aqui seria uma lista
    // longa que ninguém lembra de atualizar ao acrescentar um atributo.
  }, [user, plan, phase, daysRemaining]);
  return null;
}